import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../utils/providers/theme-context';
import { useModalEscClose } from '../../../utils/hooks/use-modal-esc';
import { useModalFocusTrap } from '../../../utils/hooks/use-modal-focus-trap';
import { CloseIcon } from '../../shared/icons';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: ReactNode;
  caption?: string;
  headerExtra?: ReactNode;
  footerActions?: ReactElement[];
  /** Width preset. Defaults to 'md'. */
  size?: 'sm' | 'md' | 'lg' | 'full';
  /** Explicit CSS width override (e.g. '520px'). Takes precedence over size. */
  customWidth?: string;
  closeOnOutsideClick?: boolean;
  closeOnEsc?: boolean;
}

const SIZE_WIDTHS: Record<NonNullable<DrawerProps['size']>, string> = {
  sm: '360px',
  md: '480px',
  lg: '640px',
  full: '100%'
};

export default function Drawer({
  isOpen,
  onClose,
  children,
  title,
  caption,
  headerExtra,
  footerActions,
  size = 'md',
  customWidth,
  closeOnOutsideClick = true,
  closeOnEsc = true
}: DrawerProps) {
  const { getColour } = useThemeContext();
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [visible, setVisible] = useState(false);
  useModalFocusTrap(isOpen, contentRef);

  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      const t = setTimeout(() => setShouldRender(false), 250);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useModalEscClose(isOpen, handleClose, closeOnEsc);

  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  if (!shouldRender) return null;

  const width = customWidth ?? SIZE_WIDTHS[size];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[999] transition-opacity duration-250"
        style={{
          backgroundColor: 'rgba(0,0,0,0.5)',
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none'
        }}
        onMouseDown={e => {
          if (closeOnOutsideClick && e.target === e.currentTarget)
            handleClose();
        }}
      />

      {/* Panel */}
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className={cn(
          'fixed inset-y-0 right-0 z-[1000] flex flex-col',
          'transition-transform duration-250 ease-[cubic-bezier(0.32,0.72,0,1)]',
          'shadow-[−8px_0_32px_rgba(0,0,0,0.18)]'
        )}
        style={{
          width,
          maxWidth: '100vw',
          backgroundColor: getColour('secondLayer'),
          borderLeft: `1px solid ${getColour('hairline')}`,
          color: getColour('normalText'),
          transform: visible ? 'translateX(0)' : 'translateX(100%)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex-shrink-0 flex items-center justify-between gap-x-4 py-4 px-6 border-b"
          style={{
            borderColor: getColour('hairline'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="min-w-0 flex flex-col">
              {title && (
                <h2 id={titleId} className="font-semibold text-base">
                  {title}
                </h2>
              )}
              {caption && (
                <p
                  className="text-xs truncate"
                  style={{ color: getColour('inactiveText') }}
                  title={caption}
                >
                  {caption}
                </p>
              )}
            </div>
            {headerExtra && (
              <>
                <div
                  className="self-stretch w-px flex-shrink-0"
                  style={{ backgroundColor: getColour('border') }}
                />
                {headerExtra}
              </>
            )}
          </div>
          <button
            type="button"
            aria-label={t('close')}
            className="border-none bg-transparent p-0 m-0 flex items-center justify-center hover:opacity-70 transition-opacity flex-shrink-0"
            style={{ color: getColour('icon') }}
            onClick={handleClose}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6">{children}</div>

        {/* Footer */}
        {footerActions && footerActions.length > 0 && (
          <div
            className="flex-shrink-0 flex justify-end items-center gap-x-2 px-6 py-3 border-t"
            style={{
              borderColor: getColour('hairline'),
              backgroundColor: getColour('secondLayer')
            }}
          >
            {footerActions.map((action, i) => (
              <div key={i}>{action}</div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .duration-250 { transition-duration: 250ms; }
      `}</style>
    </>
  );
}
